<script lang="ts">
	import { onMount } from 'svelte';
	let {
		message,
		type = 'info',
		duration = 5000,
		onClose = () => {},
		index = 0,
	} = $props<{
		message: string;
		type?: 'success' | 'error' | 'warning' | 'info';
		duration?: number;
		onClose?: () => void;
		index?: number;
	}>();

	let visible = $state(false);
	let timeoutId: ReturnType<typeof setTimeout>;

	onMount(() => {
		visible = true;

		if (duration > 0) {
			timeoutId = setTimeout(() => {
				close();
			}, duration);
		}

		return () => {
			if (timeoutId) clearTimeout(timeoutId);
		};
	});

	function close() {
		visible = false;
		setTimeout(() => {
			onClose();
		}, 300); // Wait for animation to complete
	}
</script>

{#if visible}
	<div class="toast toast-{type}" role="alert" aria-live="polite" style="--toast-index: {index}">
		<div class="toast-content">
			<span class="toast-icon">
				{#if type === 'success'}
					✓
				{:else if type === 'error'}
					✕
				{:else if type === 'warning'}
					⚠
				{:else}
					ℹ
				{/if}
			</span>
			<span class="toast-message">{message}</span>
			<button class="toast-close" onclick={close} aria-label="Close notification"> × </button>
		</div>
	</div>
{/if}

<style lang="css">
	.toast {
		position: fixed;
		top: calc(var(--lg-space-5) + var(--toast-index) * 70px);
		right: var(--lg-space-5);
		z-index: var(--lg-z-100);
		min-width: min(300px, calc(100vw - 2rem));
		max-width: min(500px, calc(100vw - 2rem));
		border-radius: var(--lg-radius-sm);
		box-shadow: var(--lg-shadow-md);
		animation: slideIn var(--lg-transition-base) ease-out;
		font-family: var(--lg-font-sans);
	}

	.toast-success {
		background: var(--lg-accent-lettuce);
		border: 1px solid var(--lg-success);
		color: var(--lg-success);
	}

	.toast-error {
		background: rgba(199, 82, 42, 0.1);
		border: 1px solid var(--lg-error);
		color: var(--lg-error);
	}

	.toast-warning {
		background: var(--lg-accent-honey);
		border: 1px solid var(--lg-warning);
		color: var(--lg-warning);
	}

	.toast-info {
		background: rgba(59, 130, 246, 0.1);
		border: 1px solid var(--lg-info);
		color: var(--lg-info);
	}

	.toast-content {
		display: flex;
		align-items: center;
		padding: var(--lg-space-3) var(--lg-space-4);
		gap: var(--lg-space-2);
	}

	.toast-icon {
		font-weight: var(--lg-font-bold);
		font-size: var(--lg-text-base);
		flex-shrink: 0;
	}

	.toast-message {
		flex: 1;
		font-size: var(--lg-text-sm);
		line-height: var(--lg-leading-snug);
	}

	.toast-close {
		background: none;
		border: none;
		font-size: var(--lg-text-xl);
		font-weight: var(--lg-font-bold);
		cursor: pointer;
		color: inherit;
		opacity: 0.7;
		padding: 0.625rem; /* 10px padding for larger touch area */
		min-width: 44px;
		min-height: 44px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.toast-close:hover,
	.toast-close:focus,
	.toast-close:active {
		opacity: 1;
	}

	.toast-close:focus-visible {
		outline: 2px solid currentColor;
		outline-offset: 2px;
	}

	@keyframes slideIn {
		from {
			transform: translateX(100%);
			opacity: 0;
		}
		to {
			transform: translateX(0);
			opacity: 1;
		}
	}
</style>
